import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowUpRight, Mail } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { TEAM } from "@/lib/team";
import { FIRM } from "@/lib/site";
import { useLang } from "@/lib/lang";

export default function Team() {
  const [selected, setSelected] = useState(null);
  const { t } = useLang();

  const memberLabel = (m) => ({
    ...m,
    roleLabel: t.team.roles[m.role] || m.role,
    items: t.team.members[m.id]?.items || m.items,
    bio: t.team.members[m.id]?.bio || m.bio,
  });

  return (
    <section data-testid="team-section" id="team" className="border-t border-hairline bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Overline>{t.team.overline}</Overline>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-navy sm:text-4xl lg:text-5xl">
              {t.team.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-md text-sm leading-relaxed text-muted-ink lg:text-base">{t.team.intro}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TEAM.map((mRaw, i) => {
            const m = memberLabel(mRaw);
            return (
              <Reveal key={mRaw.id} delay={0.05 * (i % 4)}>
                <button
                  data-testid={`team-card-${mRaw.id}`}
                  onClick={() => setSelected(m)}
                  className="group relative block w-full overflow-hidden border border-hairline bg-alabaster text-left transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-[0_24px_60px_-30px_rgba(11,29,58,0.35)]"
                  aria-label={`Apri il profilo di ${mRaw.name}`}
                >
                  <div className="relative h-56 overflow-hidden bg-navy">
                    <img
                      src={mRaw.img}
                      alt={mRaw.name}
                      className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
                    <span className="absolute bottom-4 right-4 h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl font-semibold text-navy">{mRaw.name}</h3>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-gold-dark">{m.roleLabel}</p>
                    <p className="mt-3 text-sm leading-relaxed text-muted-ink">{m.items.join(" · ")}</p>
                    <ArrowUpRight
                      size={18}
                      strokeWidth={1.25}
                      className="absolute bottom-6 right-6 text-gold opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100 md:-translate-x-1"
                    />
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md overflow-hidden border-hairline bg-alabaster p-0" data-testid="team-modal">
          {selected && (
            <div>
              <div className="relative h-40">
                <img src={selected.img} alt={selected.name} className="h-full w-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-alabaster to-transparent" />
              </div>
              <DialogHeader className="p-8 pt-2 text-left">
                <DialogTitle className="font-serif text-3xl font-semibold text-navy">{selected.name}</DialogTitle>
                <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-dark">
                  {selected.roleLabel} · {FIRM.name}
                </DialogDescription>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-ink">
                  {selected.role !== "Segreteria" ? t.team.competenze : t.team.attivita}
                </p>
                <ul className="mt-3 space-y-2">
                  {selected.items.map((it) => (
                    <li key={it} className="flex items-center gap-2.5 font-serif text-lg text-navy">
                      <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true"></span>
                      {it}
                    </li>
                  ))}
                </ul>
                {selected.bio && (
                  <p className="mt-6 text-sm leading-relaxed text-muted-ink">{selected.bio}</p>
                )}
                <a
                  href={`mailto:${FIRM.email}`}
                  data-testid="team-modal-contact"
                  className="link-gold mt-8 inline-flex w-max items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-navy"
                  onClick={() => setSelected(null)}
                >
                  <Mail size={13} strokeWidth={1.5} className="text-gold-dark" />
                  {t.team.contact}
                </a>
              </DialogHeader>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
